import { CastorService, FieldRepository } from '@aipb/gulliver/castor/data-access';
import { GeoRegisterGroupWithStat, RegisterGroupWithStat } from '@aipb/gulliver/castor/model';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { Facet, FacetCategory, SearchItem } from '@aipb/gulliver/catalog/model';
import { Language } from '@aipb/shared/util/translation';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { filter, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { SearchRepository } from '../search';
import { FacetRepository } from './facet.repository';
import { CheckedProps } from '../../../../../feature-facet/src/lib/components/facet-tree/facet-tree.store';
import { FacetTreeNode } from '../../../../../feature-facet/src/lib/model';
import { buildQueryPath, generateSolrQuery } from '../../../../../feature-facet/src/lib/facet.util';

@Injectable({
  providedIn: 'root'
})
export class FacetService {
  constructor(
    private repository: FacetRepository,
    private searchRepository: SearchRepository,
    private castor: CastorService,
    private transloco: TranslocoService,
    private fieldRepository: FieldRepository,
    private catalogEnv: CatalogEnvService
  ) {}

  public loadFacets(item: SearchItem, filterValue?: string): Observable<{ [key: string]: FacetCategory }> {
    return this.fieldRepository.selectRegister(item.coreId).pipe(
      filter((i) => i.length > 0),
      mergeMap((registerFields) => {
        let registerSub: Observable<RegisterGroupWithStat | GeoRegisterGroupWithStat>;

        if (this.catalogEnv.hasMapSearch() && item.coordinates) {
          registerSub = this.castor.geoRegisterGroupWithStat({
            coreId: item.coreId,
            query: item.search.q,
            spatialFilter: {
              spatialField: 'Coordinates',
              spatialType: 'rectangle',
              rectangle: item.coordinates.join(',')
            },
            registers: registerFields.map((rf) => ({
              fieldId: rf.fieldId,
              lang: Language.getLanguageByValue(this.transloco.getActiveLang()).iso_639_2,
              prefix: filterValue,
              limit: this.catalogEnv.env.castor.registerLimit
            }))
          });
        } else {
          registerSub = this.castor.registerGroupWithStat({
            registers: registerFields.map((rf) => ({
              fieldId: rf.fieldId,
              lang: Language.getLanguageByValue(this.transloco.getActiveLang()).iso_639_2,
              prefix: filterValue,
              limit: this.catalogEnv.env.castor.registerLimit
            })),
            coreId: item.coreId,
            query: item.search.q
          });
        }

        this.repository.setLoading(true);
        return registerSub.pipe(
          map((d) => {
            const newData = {};

            registerFields.forEach((rf) => {
              newData[rf.fieldId] = {
                id: rf.fieldId,
                label: rf.fieldName,
                items: d.facets[rf.fieldId].map((item) => ({
                  label: Object.keys(item)[0],
                  count: +item[Object.keys(item)[0]],
                  checked: false
                })),
                stat: d.stat[rf.fieldId]
              };
            });

            return newData;
          }),
          tap((fs) => {
            const facetItem = this.repository.getEntity(item.id);

            const ui = facetItem ? facetItem.ui : {};

            const newUi: { [key: string]: boolean } = {};
            for (const key of Object.keys(fs)) {
              if (key in ui) continue;

              newUi[key] = false;
            }

            this.repository.upsertFacet({
              id: item.id,
              ui: newUi,
              facets: {
                ...fs
              },
              resultCount: item.search.found,
              statResultCount: item.search.found,
              notResultCount: item.search.found,
              treeResultCount: item.search.found,
              timelineResultCount: 0,
              mapResultCount: item.search.found
            });
          }),
          finalize(() => {
            this.repository.setLoading(false);
          })
        );
      })
    );
  }

  public getFacetQuery(searchId: string): string {
    const searchItem = this.searchRepository.getSearch(searchId);
    const facets = this.repository.hasEntity(searchId) ? this.repository.getEntity(searchId).facets : {};

    const searchQuery = searchItem.search.q;

    let query = searchQuery ? `(${searchQuery})` : '';

    let subQuery = '';

    Object.keys(facets).forEach((facetCategory) => {
      const items: Facet[] = [];

      for (const key of Object.keys(facets[facetCategory].items)) {
        if (facets[facetCategory].items[key].checked) {
          items.push(facets[facetCategory].items[key]);
        }
      }

      if (items.length === 0) return;

      subQuery += '(';

      items.forEach((f, index, arr) => {
        subQuery += `${facets[facetCategory].id}:"${f.label}"`;

        if (index !== arr.length - 1) {
          subQuery += ' OR ';
        }
      });

      subQuery += ') AND ';
    });

    if (subQuery) {
      subQuery = subQuery.substring(0, subQuery.length - 5);
      query += query ? ` AND (${subQuery})` : subQuery;
    }

    return query;
  }

  public getFacetNotQuery(searchId: string): string {
    const searchItem = this.searchRepository.getSearch(searchId);
    const facets = this.repository.hasEntity(searchId) ? this.repository.getEntity(searchId).facets : {};

    const searchQuery = searchItem.search.q;

    let query = searchQuery ? `(${searchQuery})` : '';

    let subQuery = '';

    Object.keys(facets).forEach((facetCategory) => {
      const items: Facet[] = [];

      for (const key of Object.keys(facets[facetCategory].items)) {
        if (facets[facetCategory].items[key].checked) {
          items.push(facets[facetCategory].items[key]);
        }
      }

      if (items.length === 0) return;

      subQuery += '(';

      items.forEach((f, index, arr) => {
        subQuery += `${facets[facetCategory].id}:"${f.label}"`;

        if (index !== arr.length - 1) {
          subQuery += ' OR ';
        }
      });

      subQuery += ') AND ';
    });

    if (subQuery) {
      subQuery = subQuery.substring(0, subQuery.length - 5);
      query += query ? ` NOT (${subQuery})` : subQuery;
    }

    return query;
  }

  public getFacetStatQuery(searchId: string, values: { [key: string]: string | null }): string {
    const searchItem = this.searchRepository.getSearch(searchId);
    const facets = this.repository.hasEntity(searchId) ? this.repository.getEntity(searchId).facets : {};

    const searchQuery = searchItem.search.q;

    let query = searchQuery ? `(${searchQuery})` : '';

    const countvalsSubqueries = [];
    const missingSubqueries = [];

    Object.keys(values)
      .filter((facetCategory) => values[facetCategory] === 'countvals')
      .forEach((facetCategory) => {
        if (!values[facetCategory]) return;

        countvalsSubqueries.push(`${facetCategory}:[* TO *]`);
      });

    Object.keys(values)
      .filter((facetCategory) => values[facetCategory] === 'missing')
      .forEach((facetCategory) => {
        if (!values[facetCategory]) return;

        missingSubqueries.push(`${facetCategory}:[* TO *]`);
      });

    if (countvalsSubqueries.length > 0) {
      query += query ? ` AND (${countvalsSubqueries.join(' AND ')})` : countvalsSubqueries.join(' AND ');
    }

    if (missingSubqueries.length > 0) {
      query += query ? ` NOT (${missingSubqueries.join(' AND ')})` : `NOT (${missingSubqueries.join(' AND ')})`;
    }

    return query;
  }

  public getFacetTreeQuery(searchId: string, value: CheckedProps, facets: Record<string, FacetTreeNode>): string {
    const searchItem = this.searchRepository.getSearch(searchId);

    const searchQuery = searchItem.search.q;

    let query = searchQuery ? `(${searchQuery})` : '';

    const subQueries = [];

    Object.keys(value).forEach((key) => {
      const queryPath = buildQueryPath(<CheckedProps>value[key], facets[key]);
      const q = generateSolrQuery(queryPath);
      subQueries.push(q);
    });

    if (subQueries.length > 0) {
      query += query ? ` AND (${subQueries.join(' OR ')})` : subQueries.join(' AND ');
    }

    return query;
  }

  public getFacetTimelineQuery(searchId: string, fieldId: string, start: Date, end: Date): string {
    const searchItem = this.searchRepository.getSearch(searchId);

    const searchQuery = searchItem.search.q;

    let query = searchQuery ? `(${searchQuery})` : '';

    query += (query ? ` AND ` : '') + `${fieldId}:[${start.toISOString()} TO ${end.toISOString()}]`;

    return query;
  }

  clear() {
    this.repository.reset();
  }
}
